import { tasksIcon, toClassicIcon } from "common/defaultIcons";
import {
  LAYOUTS,
  MENU_SECTIONS,
  MODULE_SETTINGS,
  TEACHER_ROLES,
  USER_ROLES,
} from "shared/constants";
import { PORTFOLIO_ABILITIES } from "portfolio/constants";
import { PARTNER_API_ROUTE_NAMES } from "partnerApi/routes/routeNames";
import type { AppRouteRecordRaw } from "shared/types";
import type { RouteLocationNormalized, RouteLocationRaw } from "vue-router";
import { PORTFOLIO_ROUTE_NAMES } from "./routeNames";

/**
 * Sends an old `/tasks/manage/<library>/...` URL to its `/tasks/library/<kind>/...`
 * successor, keeping whatever followed the library segment (`:taskId`,
 * `create`, `edit/:taskId`) and the query string.
 */
const redirectToLibrary =
  (kind: "school" | "public") =>
  (to: RouteLocationNormalized): RouteLocationRaw => {
    const rest = Array.isArray(to.params.pathMatch)
      ? to.params.pathMatch
      : [to.params.pathMatch].filter((segment) => segment !== undefined);

    return {
      path: ["/tasks/library", kind, ...rest].join("/"),
      query: to.query,
    };
  };

// The Taken menu has three entries, mirroring Actieplannen (#1185):
// - Taken overzicht: the scheduled tasks, grouped per task, split in an
//   Openstaand and an Afgerond tab.
// - Taken beoordelen: the individual hand-ins, split in Te beoordelen and
//   Beoordeeld.
// - Takenbieb: the school's and the public library (and, for the webmaster,
//   the partner API tabs). Slated to move to the catalogus later.
const routes: AppRouteRecordRaw[] = [
  {
    name: "teacherTasksIndex",
    path: "/tasks",
    component: () => import("views/EmptyView.vue"),
    redirect: { name: "teacherManageTasks" },
    meta: {
      title: "Taken",
      roleRequired: TEACHER_ROLES,
      isMenuItem: true,
      inDropdown: true,
      hasSubmenu: true,
      menuOrder: 4,
      icon: toClassicIcon(tasksIcon),
      menuSection: MENU_SECTIONS.ORGANISE,
      settingRequired: [MODULE_SETTINGS.PORTFOLIO],
      layout: LAYOUTS.MAIN_NO_WRAPPER,
    },
    children: [
      // Overview routes
      {
        name: "teacherManageTasks",
        path: "manage",
        component: () => import("views/EmptyView.vue"),
        redirect: { name: PORTFOLIO_ROUTE_NAMES.TEACHER_OPEN_SCHEDULED_TASKS },
        meta: {
          title: "Taken overzicht",
          roleRequired: TEACHER_ROLES,
          isMenuItem: true,
          inDropdown: true,
          hasSubmenu: true,
          hideSidebarSubmenu: true,
          menuSection: MENU_SECTIONS.ORGANISE,
          settingRequired: [MODULE_SETTINGS.PORTFOLIO],
        },
        children: [
          {
            name: PORTFOLIO_ROUTE_NAMES.TEACHER_SCHEDULED_TASK,
            path: "scheduled/:taskId",
            component: () =>
              import("portfolio/pages/TeacherScheduledTaskDetailPage.vue"),
            meta: {
              title: "Taak details",
              roleRequired: TEACHER_ROLES,
              settingRequired: [MODULE_SETTINGS.PORTFOLIO],
              hideSubmenu: true,
              hideTitle: true,
            },
          },
          // Kept as a redirect: the create/edit/detail pages and the header
          // actions all send the teacher back here after saving.
          {
            name: "teacherScheduledTasks",
            path: "scheduled",
            redirect: {
              name: PORTFOLIO_ROUTE_NAMES.TEACHER_OPEN_SCHEDULED_TASKS,
            },
          },
          {
            name: PORTFOLIO_ROUTE_NAMES.TEACHER_OPEN_SCHEDULED_TASKS,
            path: "scheduled/open",
            component: () =>
              import("portfolio/pages/TeacherOpenScheduledTasksPage.vue"),
            meta: {
              title: "Taken overzicht",
              tabLabel: "Openstaande taken",
              roleRequired: TEACHER_ROLES,
              inDropdown: true,
              menuSection: MENU_SECTIONS.ORGANISE,
              settingRequired: [MODULE_SETTINGS.PORTFOLIO],
            },
          },
          {
            name: PORTFOLIO_ROUTE_NAMES.TEACHER_COMPLETED_SCHEDULED_TASKS,
            path: "scheduled/completed",
            component: () =>
              import("portfolio/pages/TeacherCompletedScheduledTasksPage.vue"),
            meta: {
              title: "Taken overzicht",
              tabLabel: "Afgeronde taken",
              roleRequired: TEACHER_ROLES,
              inDropdown: true,
              menuSection: MENU_SECTIONS.ORGANISE,
              settingRequired: [MODULE_SETTINGS.PORTFOLIO],
            },
          },
          {
            name: "teacherScheduledTaskCreate",
            path: "scheduled/create",
            component: () =>
              import("portfolio/pages/TeacherScheduledTaskCreatePage.vue"),
            meta: {
              title: "Taak aanmaken",
              roleRequired: TEACHER_ROLES,
              settingRequired: [MODULE_SETTINGS.PORTFOLIO],
              abilityRequired: PORTFOLIO_ABILITIES.CAN_CREATE_TASK,
              hideSubmenu: true,
              hideTitle: true,
            },
          },
          {
            name: "teacherScheduledTaskEdit",
            path: "scheduled/edit/:taskId",
            component: () =>
              import("portfolio/pages/TeacherScheduledTaskEditPage.vue"),
            meta: {
              title: "Taak bewerken",
              roleRequired: TEACHER_ROLES,
              settingRequired: [MODULE_SETTINGS.PORTFOLIO],
              hideSubmenu: true,
              hideTitle: true,
            },
          },
          // The libraries used to live under the overview. Old links and
          // bookmarks — the lists, but also a task's detail, create and edit
          // pages — land on the new Takenbieb paths.
          {
            path: "library/:pathMatch(.*)*",
            redirect: redirectToLibrary("school"),
          },
          {
            path: "public-library/:pathMatch(.*)*",
            redirect: redirectToLibrary("public"),
          },
          {
            path: "api-task-requests",
            redirect: { name: PARTNER_API_ROUTE_NAMES.TASK_REQUESTS },
          },
          {
            path: "taxonomy",
            redirect: { name: PARTNER_API_ROUTE_NAMES.TAXONOMY },
          },
        ],
      },
      {
        name: "teacherReviewTasks",
        path: "review",
        component: () => import("views/EmptyView.vue"),
        redirect: { name: "teacherPendingReviewTasks" },
        meta: {
          title: "Taken beoordelen",
          roleRequired: TEACHER_ROLES,
          isMenuItem: true,
          inDropdown: true,
          hasSubmenu: true,
          hideSidebarSubmenu: true,
          menuSection: MENU_SECTIONS.ORGANISE,
          settingRequired: [MODULE_SETTINGS.PORTFOLIO],
        },
        children: [
          {
            name: "teacherPendingReviewTasks",
            path: "pending",
            component: () =>
              import("portfolio/pages/TeacherPendingAssessmentsPage.vue"),
            meta: {
              title: "Taken beoordelen",
              tabLabel: "Te beoordelen",
              roleRequired: TEACHER_ROLES,
              inDropdown: true,
              menuSection: MENU_SECTIONS.ORGANISE,
              settingRequired: [MODULE_SETTINGS.PORTFOLIO],
            },
          },
          {
            name: "teacherReviewedTasks",
            path: "completed",
            component: () =>
              import("portfolio/pages/TeacherReviewedAssessmentsPage.vue"),
            meta: {
              title: "Taken beoordelen",
              tabLabel: "Beoordeeld",
              roleRequired: TEACHER_ROLES,
              inDropdown: true,
              menuSection: MENU_SECTIONS.ORGANISE,
              settingRequired: [MODULE_SETTINGS.PORTFOLIO],
            },
          },
        ],
      },
      // Library routes
      {
        name: PORTFOLIO_ROUTE_NAMES.TEACHER_TASK_LIBRARY_INDEX,
        path: "library",
        component: () => import("views/EmptyView.vue"),
        redirect: { name: "teacherTaskLibrary" },
        meta: {
          title: "Takenbieb",
          roleRequired: TEACHER_ROLES,
          isMenuItem: true,
          inDropdown: true,
          hasSubmenu: true,
          hideSidebarSubmenu: true,
          menuSection: MENU_SECTIONS.ORGANISE,
          settingRequired: [MODULE_SETTINGS.PORTFOLIO],
        },
        children: [
          {
            name: "teacherLibraryTask",
            path: "school/:taskId",
            component: () =>
              import("portfolio/pages/TeacherLibraryTaskDetailPage.vue"),
            meta: {
              title: "Taak details",
              roleRequired: TEACHER_ROLES,
              settingRequired: [MODULE_SETTINGS.PORTFOLIO],
              hideSubmenu: true,
              hideTitle: true,
            },
          },
          {
            name: "teacherPublicLibraryTask",
            path: "public/:taskId",
            component: () =>
              import("portfolio/pages/TeacherPublicLibraryTaskDetailPage.vue"),
            meta: {
              title: "Taak details",
              roleRequired: TEACHER_ROLES,
              settingRequired: [MODULE_SETTINGS.PORTFOLIO],
              hideSubmenu: true,
              hideTitle: true,
            },
          },
          {
            name: "teacherTaskLibrary",
            path: "school",
            component: () =>
              import("portfolio/pages/TeacherTaskLibraryPage.vue"),
            meta: {
              title: "Takenbieb",
              tabLabel: "Takenbibliotheek",
              roleRequired: TEACHER_ROLES,
              inDropdown: true,
              menuSection: MENU_SECTIONS.ORGANISE,
              settingRequired: [MODULE_SETTINGS.PORTFOLIO],
            },
          },
          {
            name: "teacherLibraryTaskCreate",
            path: "school/create",
            component: () =>
              import("portfolio/pages/TeacherLibraryTaskCreatePage.vue"),
            meta: {
              title: "Taak aanmaken",
              roleRequired: TEACHER_ROLES,
              settingRequired: [MODULE_SETTINGS.PORTFOLIO],
              abilityRequired: PORTFOLIO_ABILITIES.CAN_CREATE_TASK,
              hideSubmenu: true,
              hideTitle: true,
            },
          },
          {
            name: "teacherLibraryTaskEdit",
            path: "school/edit/:taskId",
            component: () =>
              import("portfolio/pages/TeacherLibraryTaskEditPage.vue"),
            meta: {
              title: "Taak bewerken",
              roleRequired: TEACHER_ROLES,
              settingRequired: [MODULE_SETTINGS.PORTFOLIO],
              hideSubmenu: true,
              hideTitle: true,
            },
          },
          {
            name: "teacherPublicTaskLibrary",
            path: "public",
            component: () =>
              import("portfolio/pages/TeacherPublicTaskLibraryPage.vue"),
            meta: {
              title: "Takenbieb",
              tabLabel: "Openbare takenbibliotheek",
              roleRequired: TEACHER_ROLES,
              inDropdown: true,
              menuSection: MENU_SECTIONS.ORGANISE,
              settingRequired: [MODULE_SETTINGS.PORTFOLIO],
            },
          },
          {
            name: "teacherPublicLibraryTaskCreate",
            path: "public/create",
            component: () =>
              import("portfolio/pages/TeacherPublicLibraryTaskCreatePage.vue"),
            meta: {
              title: "Taak aanmaken",
              roleRequired: TEACHER_ROLES,
              settingRequired: [MODULE_SETTINGS.PORTFOLIO],
              abilityRequired: PORTFOLIO_ABILITIES.CAN_CREATE_PUBLIC_TASK,
              hideSubmenu: true,
              hideTitle: true,
            },
          },
          {
            name: "teacherPublicLibraryTaskEdit",
            path: "public/edit/:taskId",
            component: () =>
              import("portfolio/pages/TeacherPublicLibraryTaskEditPage.vue"),
            meta: {
              title: "Taak bewerken",
              roleRequired: TEACHER_ROLES,
              settingRequired: [MODULE_SETTINGS.PORTFOLIO],
              hideSubmenu: true,
              hideTitle: true,
            },
          },
          {
            name: PARTNER_API_ROUTE_NAMES.TASK_REQUESTS,
            path: "api-task-requests",
            component: () =>
              import("partnerApi/pages/WebmasterTaskRequestPage.vue"),
            meta: {
              title: "Takenbieb",
              tabLabel: "API-taken beheren",
              roleRequired: [USER_ROLES.WEBMASTER],
              inDropdown: true,
              menuSection: MENU_SECTIONS.ORGANISE,
              settingRequired: [MODULE_SETTINGS.PORTFOLIO],
            },
          },
          {
            name: PARTNER_API_ROUTE_NAMES.TAXONOMY,
            path: "taxonomy",
            component: () =>
              import("partnerApi/pages/WebmasterTaxonomyPage.vue"),
            meta: {
              title: "Takenbieb",
              tabLabel: "Standaard sets & mapping",
              roleRequired: [USER_ROLES.WEBMASTER],
              inDropdown: true,
              menuSection: MENU_SECTIONS.ORGANISE,
              settingRequired: [MODULE_SETTINGS.PORTFOLIO],
            },
          },
        ],
      },
    ],
  },
];

export default routes;
